/**
 * optionsMenu — centralized option-dropdown markup, icons, and row-menu behavior.
 *
 * Single source of truth for the "kebab → popup action menu" pattern that used
 * to be copy-pasted across the app. It backs both families:
 *   - the {@link OptionsDropdown} component (detail-page header menus), and
 *   - the list/sidebar row kebabs (Sessions, Practices, Partner-notes,
 *     Discovery threads, Sidebar recents).
 *
 * It replaces 5 near-identical inline markup builders, 5 duplicate kebab-glyph
 * constants, and 4 clone open/close controllers with:
 *   - {@link KEBAB_GLYPH}     — the one shared trigger glyph
 *   - {@link menuIcon}        — action → leading icon (keyed off action, not label)
 *   - {@link renderMenuItems} — the `.options-dropdown__item` button list
 *   - {@link renderRowKebab}  — the full hover-revealed row kebab + menu
 *   - {@link RowMenuController}— open/close/outside-click/hover-out for row menus
 *
 * @module components/optionsMenu
 */

import { escapeHtml } from '../core/utils.js';
import { Tooltip } from '../utils/Tooltip.js';

/**
 * The shared kebab (meatball) trigger glyph — a horizontal three-dot. One glyph
 * everywhere so triggers can't drift (previously 5 hand-copied variants, two of
 * them vertical).
 * @type {string}
 */
export const KEBAB_GLYPH = `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><circle cx="2.5" cy="8" r="1.2"/><circle cx="8" cy="8" r="1.2"/><circle cx="13.5" cy="8" r="1.2"/></svg>`;

/**
 * Menu action verb → sprite symbol id (minus the `icon-` prefix). Keyed off the
 * ACTION, not the label, so Star/Unstar (both action `star`) and
 * Archive/Unarchive (both action `archive`) each resolve to one stable icon.
 */
const MENU_ICON = {
  discuss: 'chat-bubble',
  copy: 'copy',
  share: 'share',
  star: 'star',
  archive: 'archive',
  unarchive: 'unarchive',
  restore: 'unarchive',
  revoke: 'revoke',
  delete: 'trash',
  end_session: 'signout',
  dismiss: 'eye-off',
};

/**
 * Strip the surface prefix (`row-`, `select-`) an action string may carry so
 * `row-delete`, `select-delete`, and `delete` all map to the same icon.
 * @param {string} action
 * @returns {string}
 */
function baseAction(action) {
  return String(action || '').replace(/^(row|select)-/, '');
}

/**
 * Leading-icon SVG for a menu action, or `''` when neither an override nor the
 * action maps to a symbol. The icon inherits the item's `color` (via CSS), so a
 * danger item's red text recolors its icon automatically.
 * @param {string} action - Menu action (prefixed or bare)
 * @param {string} [override] - Explicit sprite symbol suffix (without `icon-`);
 *   wins over the action map. Used for state-dependent icons, e.g. Star →
 *   `star-outline` (hollow) vs Unstar → `star` (solid).
 * @returns {string} Inline `<svg>` markup, or empty string
 */
export function menuIcon(action, override) {
  const symbol = override || MENU_ICON[baseAction(action)];
  return symbol
    ? `<svg class="options-dropdown__item-icon" width="16" height="16" aria-hidden="true"><use href="#icon-${symbol}"/></svg>`
    : '';
}

/**
 * Serialize a data-attribute map to an escaped attribute string (no leading
 * space). Values are HTML-escaped here, so callers pass RAW ids.
 * @param {Object<string,string>} attrs
 * @returns {string}
 */
function attrString(attrs) {
  return Object.entries(attrs || {})
    .map(([k, v]) => `${k}="${attr(v)}"`)
    .join(' ');
}

/**
 * Escape a value for use inside a double-quoted HTML attribute.
 * @param {*} value
 * @returns {string}
 */
function attr(value) {
  return escapeHtml(String(value));
}

/**
 * Render the menu-item buttons for an option menu.
 *
 * The label sits in a whitespace-free `<span>` after the icon so each button's
 * `textContent` is exactly the label (tests and a11y read it).
 *
 * @param {Array<Object>} items - Menu items
 * @param {string} items[].action - Action identifier (drives the icon)
 * @param {string} items[].label - Display label
 * @param {string} [items[].variant] - `'danger'` for destructive actions
 * @param {string} [items[].icon] - Explicit sprite symbol suffix, overriding the
 *   action→icon map (e.g. Star → `star-outline`, Unstar → `star`)
 * @param {boolean} [items[].disabled] - Renders the row inert (bare `disabled`
 *   attribute + `--disabled` class). Output is byte-identical to before for
 *   items without the key — never interpolate the value (`disabled="false"`
 *   still disables per the HTML spec).
 * @param {string} [items[].hint] - Optional trailing muted-text hint rendered
 *   after the label (e.g. "Opens after your key takeaways" on a locked Create
 *   menu row). Output is byte-identical to before for items without the key.
 * @param {Object<string,string>} [dataAttrs] - Extra data-attrs stamped on every
 *   item (e.g. the row id), passed as RAW values (escaped here)
 * @returns {string} HTML string of `.options-dropdown__item` buttons
 */
export function renderMenuItems(items, dataAttrs = {}) {
  const attrs = attrString(dataAttrs);
  const suffix = attrs ? ` ${attrs}` : '';
  // "Discuss", whenever present, is always the top option. Sort is stable
  // (ES2019+), so every other item keeps its caller-given order.
  const ordered = [...(items || [])].sort(
    (a, b) => (baseAction(b.action) === 'discuss') - (baseAction(a.action) === 'discuss')
  );
  return ordered
    .map((item, i) => {
      // Separate the terminal Delete from the actions above it (mock parity) —
      // only when something precedes it, so a Delete-only menu gets no stray rule.
      const divider = baseAction(item.action) === 'delete' && i > 0
        ? '<div class="options-dropdown__divider" role="separator"></div>'
        : '';
      const cls = `options-dropdown__item${item.variant === 'danger' ? ' options-dropdown__item--danger' : ''}${item.disabled ? ' options-dropdown__item--disabled' : ''}`;
      const hint = item.hint
        ? `<span class="options-dropdown__item-hint">${escapeHtml(item.hint)}</span>`
        : '';
      return `${divider}<button type="button" role="menuitem" class="${cls}" data-action="${escapeHtml(item.action)}"${item.disabled ? ' disabled' : ''}${suffix}>${menuIcon(item.action, item.icon)}<span class="options-dropdown__item-label">${escapeHtml(item.label)}</span>${hint}</button>`;
    })
    .join('');
}

/**
 * Render a sectioned RADIO menu — the body of ResultsFilterBar's filter menu.
 *
 * Deliberately a sibling of {@link renderMenuItems} rather than an option on
 * it: action menus and radio menus share only their CSS. Action rows are
 * `menuitem`s with a leading action icon, a danger variant, a terminal
 * divider, and Discuss-floats-to-top ordering; radio rows are
 * `menuitemradio`s with `aria-checked` and a trailing checkmark, in caller
 * order. Merging the two would put all eight `renderMenuItems` call sites at
 * risk to serve one new consumer.
 *
 * Every row carries `aria-checked` (not only the selected one) so assistive
 * tech reads the whole group's state, and each group is a `role="group"` with
 * an accessible name — a `role="presentation"` header would be stripped from
 * the a11y tree, hiding exactly the label a sighted user can see.
 *
 * Row actions are namespaced `rfb-menu`: all four list sections run a
 * delegated `data-action` switch on a container that CONTAINS the bar mount,
 * so a bare `star`/`archive`/`delete` here would fall through into their
 * bulk-action handlers.
 *
 * @param {Array<Object>} groups - Menu groups, rendered in array order
 * @param {string} [groups[].label] - Group header; omit for an unheaded group
 *   (the trailing starred toggle), in which case `ariaLabel` names it
 * @param {string} [groups[].ariaLabel] - Accessible name; defaults to `label`
 * @param {string} groups[].id - Group id, echoed as `data-rfb-filter`
 * @param {Array<Object>} groups[].items - Rows
 * @param {string} groups[].items[].value - Row value, echoed as `data-rfb-value`
 * @param {string} groups[].items[].label - Display label
 * @param {boolean} [groups[].items[].selected] - Renders the trailing check
 * @param {string} [groups[].items[].icon] - Optional leading sprite symbol
 * @returns {string} HTML string of `.options-dropdown__group` blocks
 */
export function renderMenuSections(groups) {
  return (groups || [])
    .map((group) => {
      const name = group.ariaLabel || group.label || '';
      const header = group.label
        ? `<div class="options-dropdown__section" aria-hidden="true">${escapeHtml(group.label)}</div>`
        : '';
      const rows = (group.items || [])
        .map((item) => {
          const leading = item.icon
            ? `<svg class="options-dropdown__item-icon" width="16" height="16" aria-hidden="true"><use href="#icon-${attr(item.icon)}"/></svg>`
            : '';
          const check = item.selected
            ? `<svg class="options-dropdown__item-check" viewBox="0 0 3.2123498 2.408984" aria-hidden="true"><use href="#icon-checkmark"/></svg>`
            : '';
          return `<button type="button" role="menuitemradio" aria-checked="${item.selected ? 'true' : 'false'}"`
            + ` class="options-dropdown__item" data-action="rfb-menu"`
            + ` data-rfb-filter="${attr(group.id)}"`
            + ` data-rfb-value="${attr(item.value)}">`
            + `${leading}<span class="options-dropdown__item-label">${escapeHtml(item.label)}</span>${check}</button>`;
        })
        .join('');
      return `<div class="options-dropdown__group" role="group" aria-label="${attr(name)}">${header}${rows}</div>`;
    })
    .join('');
}

/**
 * Render the select-mode bulk toolbar shared by Search, Practices, Notes, and
 * Discovery threads.
 *
 * A pure string builder, NOT a component: every one of those sections destroys
 * its {@link ResultsFilterBar} before entering select mode and then owns the
 * mount directly, rewriting it via `innerHTML` on each checkbox tick. Nothing
 * here is stateful.
 *
 * Buttons emit their `data-action` verbatim and bind NO listeners — clicks
 * bubble to the section's existing delegated handler, which already routes
 * `select-*` to the same `_runBulk` entry points the row kebabs use.
 *
 * `actions` is a variable-length ordered array with no fixed slots: Notes'
 * sent list puts Revoke where the others put Star, and Practices swaps Archive
 * for Restore on the archived view. `disabled` arrives fully computed, because
 * the sections gate it inconsistently (Practices hard-disables Archive off the
 * active view; Notes has no in-flight guard at all).
 *
 * @param {Object} config
 * @param {number} config.count - Selected-item count shown on the left
 * @param {Array<Object>} config.actions - Buttons, in render order
 * @param {string} config.actions[].action - `data-action` value, e.g. `select-star`
 * @param {string} config.actions[].label - Accessible name (Star/Unstar, …)
 * @param {string} config.actions[].icon - Sprite symbol suffix (without `icon-`)
 * @param {boolean} [config.actions[].disabled] - Fully-computed disabled state
 * @returns {string} HTML string
 */
export function renderSelectToolbar({ count, actions }) {
  const buttons = (actions || [])
    .map((action) => {
      const disabled = action.disabled ? ' disabled' : '';
      return `<button type="button" class="results-filter-bar__action results-filter-bar__action--square"`
        + ` data-action="${attr(action.action)}"`
        + ` aria-label="${attr(action.label)}" data-tooltip="${attr(action.label)}"${disabled}>`
        + `<svg width="22" height="22" aria-hidden="true"><use href="#icon-${attr(action.icon)}"/></svg>`
        + `</button>`;
    })
    .join('');
  return `
      <div class="results-filter-bar results-filter-bar--select">
        <div class="results-filter-bar__left">
          <span class="results-filter-bar__count">
            <span class="results-filter-bar__count-num">${Number(count) || 0}</span> selected
          </span>
        </div>
        <div class="results-filter-bar__right">
          ${buttons}
        </div>
      </div>
    `;
}

/**
 * Cleanup functions for the tooltips wired onto each select-toolbar mount, so
 * {@link mountSelectToolbar} can tear down the previous render's before
 * replacing the DOM. Keyed by mount element and weakly held, so a section that
 * is destroyed drops its entry automatically.
 * @type {WeakMap<HTMLElement, Function>}
 */
const selectToolbarTooltips = new WeakMap();

/**
 * Render the select toolbar into a mount and wire its tooltips.
 *
 * Use this rather than assigning {@link renderSelectToolbar}'s output directly:
 * the sections re-render this toolbar on every checkbox tick, and the tooltip
 * element lives outside the button (it is removed on the button's `mouseleave`).
 * Replacing the mount's innerHTML while a button is hovered would therefore
 * strand a visible tooltip with no element left to fire `mouseleave`. This
 * disposes the previous render's tooltips first.
 *
 * @param {HTMLElement} mountEl - The section's bar mount
 * @param {Object} config - See {@link renderSelectToolbar}
 * @returns {Function} Cleanup for the tooltips just wired
 */
export function mountSelectToolbar(mountEl, config) {
  if (!mountEl) return () => {};
  selectToolbarTooltips.get(mountEl)?.();
  mountEl.innerHTML = renderSelectToolbar(config);
  const cleanup = Tooltip.setupAll(mountEl, '[data-tooltip]', { position: 'below' });
  selectToolbarTooltips.set(mountEl, cleanup);
  return cleanup;
}

/**
 * Nudge an OPEN, right-anchored menu back inside the viewport.
 *
 * `.options-dropdown__menu` is `position: absolute; right: 0`, so it grows
 * LEFTWARD from its trigger. That overflows the left edge of the screen whenever
 * the menu is wider than the distance from the viewport's left edge to the
 * trigger — which the mobile menu step-up made common in two places:
 *   - detail-page menus, where `.right-panel__actions` sits several buttons in
 *     from the right (star + options + secondary + primary)
 *   - `/experiments`, where the "New experiment" primary pill pushes the Filter and
 *     Select triggers toward the middle of the bar
 * `max-width` cannot fix this: the menu is not too wide for the viewport, it is
 * in the wrong place. So measure and translate.
 *
 * `transform` rather than a `left`/`right` rewrite: it does not touch layout, it
 * cannot fight the anchoring rule in CSS, and clearing it fully restores the
 * anchored position. The transform is reset before measuring, so repeated opens
 * of the same (persistent) menu element never compound.
 *
 * MUST be called AFTER the menu is visible — `getBoundingClientRect()` on a
 * `display: none` element is all zeros and the guard would silently no-op.
 *
 * Clamping to the viewport is also what keeps the right-panel menus inside
 * `.right-panel`, which is `overflow: hidden` — on mobile that panel is
 * `width: 100vw` at `left: 0`, so viewport-safe is panel-safe. On desktop the
 * panel is narrower than the viewport; a menu that overflows the panel but not
 * the screen is out of scope here (no reported case, and the desktop menu keeps
 * its narrower 140px floor).
 *
 * NOT for the portal-reparented sidebar recents menu — that one is
 * `position: fixed` and does its own clamping in
 * `SidebarRecentsController._positionMenu`.
 *
 * @param {HTMLElement} menu - The open `.options-dropdown__menu` element
 * @param {number} [gap=8] - Minimum breathing room from the viewport edge
 */
export function keepMenuInViewport(menu, gap = 8) {
  if (!menu) return;
  menu.style.transform = '';
  const rect = menu.getBoundingClientRect();
  if (!rect.width) return;

  let shift = 0;
  if (rect.left < gap) {
    // Left-edge overflow is the common case and takes priority: the menu can
    // never be wider than the viewport (its `max-width` is `100vw - 24px`), so
    // fixing the left edge cannot push the right edge out.
    shift = gap - rect.left;
  } else if (rect.right > window.innerWidth - gap) {
    shift = window.innerWidth - gap - rect.right;
  }

  if (shift) menu.style.transform = `translateX(${Math.round(shift)}px)`;
}

/**
 * Position an OPEN menu with `position: fixed`, below and right-aligned to
 * its trigger — escapes an ancestor's `overflow: auto` clipping entirely
 * (fixed positioning is relative to the viewport, not any scrolling
 * ancestor) without reparenting the menu into the DOM. A first attempt at
 * this bug flipped the menu upward when clipped below; the product owner
 * rejected that — on a short scrollport a 4-item menu can be clipped in
 * BOTH directions (any position bounded by the container's own box loses),
 * and the stated preference is that these menus always open below the
 * trigger, never above. `keepMenuInViewport` does NOT apply to a fixed-mode
 * menu (it assumes `position: absolute` + a `transform`) — skip it when
 * this is used; RowMenuController does that automatically for its opt-in
 * `clipContainer` consumers.
 *
 * Vertical adjustment is a viewport-bottom safety net ONLY, and it never
 * flips: if there truly is no room below (rare — the viewport is normally
 * far taller than these 4-item menus), the top is pulled up just enough to
 * fit on screen, still opening downward from wherever it lands.
 *
 * PRECONDITION, verified true for the rows this backs at the time this was
 * written: no ancestor of the row may carry a `transform`, `filter`, or
 * `will-change` that establishes a new containing block — any of those
 * would re-scope `position: fixed` to that ancestor instead of the
 * viewport, silently breaking this (the dashboard page's own transitions
 * are opacity-only; the translateZ/will-change rules in dashboard.css
 * target nav cards, not any ancestor of the recommended-activity rows). If
 * that precondition is ever violated, the fix is NOT another positioning
 * trick — reparent the menu to `document.body`, mirroring
 * `SidebarRecentsController`'s existing portal pattern for its own
 * `position: fixed` menu.
 *
 * MUST be called AFTER the menu is visible — `getBoundingClientRect()` on a
 * `display: none` element is all zeros and this would silently no-op.
 *
 * @param {HTMLElement} menu - The open `.options-dropdown__menu` element
 * @param {HTMLElement} anchorEl - The trigger the menu is anchored to
 * @param {number} [gap=8] - Minimum breathing room from the viewport edge
 * @param {'right'|'left'} [align='right'] - Which edge of the anchor the menu
 *   hangs from before viewport clamping. 'right' matches the stylesheet's
 *   default (`right: 0`, used by right-edge row kebabs); 'left' matches a
 *   caller whose base CSS instead sets `left: 0` (e.g. a left-aligned
 *   trigger like the dashboard view-switcher).
 */
export function positionMenuFixedBelow(menu, anchorEl, gap = 8, align = 'right') {
  if (!menu || !anchorEl) return;
  menu.style.position = 'fixed';
  menu.style.right = 'auto'; // clears the base rule's `right: 0` — left/top now own placement

  const anchorRect = anchorEl.getBoundingClientRect();
  const menuRect = menu.getBoundingClientRect();
  if (!menuRect.width) return; // still hidden pre-visible — nothing to clamp yet

  let left = align === 'left' ? anchorRect.left : anchorRect.right - menuRect.width;
  left = Math.max(gap, Math.min(left, window.innerWidth - gap - menuRect.width));

  let top = anchorRect.bottom + 4;
  const maxTop = window.innerHeight - gap - menuRect.height;
  if (top > maxTop) top = Math.max(gap, maxTop); // pulled up to fit — still opens downward from here

  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
}

/**
 * Render a full hover-revealed row kebab: the `.note-row__actions` wrapper, the
 * `.note-row__options` trigger (shared {@link KEBAB_GLYPH}), and the hidden
 * `.options-dropdown__menu`. Drop-in replacement for the per-section inline
 * builders; behavior is driven by {@link RowMenuController}.
 *
 * @param {Object} config
 * @param {Array<Object>} config.items - Menu items (see {@link renderMenuItems})
 * @param {Object<string,string>} [config.dataAttrs] - Row-id data-attrs stamped on
 *   the trigger and every item (RAW values, escaped here)
 * @param {string} [config.ariaLabel='Options'] - Accessible label for the trigger
 * @returns {string} HTML string
 */
export function renderRowKebab({ items, dataAttrs = {}, ariaLabel = 'Options' }) {
  const attrs = attrString(dataAttrs);
  const suffix = attrs ? ` ${attrs}` : '';
  return `
      <span class="note-row__actions">
        <button type="button" class="note-row__options" data-action="row-options"${suffix}
                aria-haspopup="menu" aria-expanded="false" aria-label="${escapeHtml(ariaLabel)}">${KEBAB_GLYPH}</button>
        <div class="options-dropdown__menu options-dropdown__menu--hidden" role="menu">
          ${renderMenuItems(items, dataAttrs)}
        </div>
      </span>
    `;
}

/**
 * Open/close controller for a section's hover-revealed row kebab menus. One
 * menu open at a time; a document click or (optionally) a hover-out closes it.
 * The section keeps owning its own delegated action dispatch — this only drives
 * the menu's visibility — so it stays decoupled from each section's semantics.
 *
 * The menu is a DOM child of its row (no portal); the genuinely-divergent
 * portal-reparenting variant (SidebarRecentsController) is not built on this.
 *
 * @example
 * this._rowMenu = new RowMenuController({
 *   container: () => this.container,
 *   keyAttr: 'data-item-key',
 *   rowSelector: '.session-card',
 *   hoverClose: true,
 * });
 * // in the delegated handler:
 * //   case 'row-options': this._rowMenu.toggle(target); break;
 * //   case 'row-delete':  this._rowMenu.close(); ...
 */
export class RowMenuController {
  /**
   * @param {Object} config
   * @param {HTMLElement|(() => HTMLElement)} config.container - The list container
   *   (or a getter) used to re-find the open row on close
   * @param {string} config.keyAttr - Data-attr on the trigger carrying the row id
   * @param {string} config.rowSelector - Selector for the row/card element
   * @param {boolean} [config.hoverClose=false] - Also close on the row's mouseleave
   * @param {string} [config.openClass='note-row--menu-open'] - Class toggled on the open row
   * @param {HTMLElement|(() => HTMLElement)} [config.clipContainer] - Opt-in: the
   *   list's own `overflow: auto` scrollport, when one exists and can clip the
   *   menu vertically (z-index cannot beat ancestor overflow clipping the way
   *   it beats stacking order). When provided, `toggle()` switches the menu to
   *   `position: fixed` via {@link positionMenuFixedBelow} instead of the
   *   default `keepMenuInViewport` handling, and wires close-on-scroll (on
   *   this container AND `window`) plus close-on-resize — a fixed menu must
   *   not float over a scrolled-away row. Omit for consumers with no clipping
   *   ancestor — the five pre-existing RowMenuController consumers all omit
   *   this and are unaffected by any of the above.
   */
  constructor({ container, keyAttr, rowSelector, hoverClose = false, openClass = 'note-row--menu-open', clipContainer }) {
    this._getContainer = typeof container === 'function' ? container : () => container;
    this._getClipContainer = clipContainer
      ? (typeof clipContainer === 'function' ? clipContainer : () => clipContainer)
      : null;
    this.keyAttr = keyAttr;
    this.rowSelector = rowSelector;
    this.hoverClose = hoverClose;
    this.openClass = openClass;

    this._openKey = null;
    this._rowEl = null;
    this._boundClose = () => this.close();
  }

  /** @returns {boolean} Whether a menu is currently open. */
  get isOpen() {
    return this._openKey !== null;
  }

  /**
   * Toggle the menu on one row (closing any other first). Re-clicking the same
   * row's kebab closes it.
   * @param {HTMLElement} button - The clicked kebab trigger
   */
  toggle(button) {
    const key = button.getAttribute(this.keyAttr);
    const wasOpen = this._openKey === key;
    this.close();
    if (wasOpen || !key) return;

    const row = button.closest(this.rowSelector);
    const menu = button.parentElement?.querySelector('.options-dropdown__menu');
    if (!row || !menu) return;

    this._openKey = key;
    this._rowEl = row;
    row.classList.add(this.openClass);
    button.setAttribute('aria-expanded', 'true');
    menu.classList.remove('options-dropdown__menu--hidden');
    // After the class is removed — the menu measures 0×0 while hidden. Row
    // kebabs sit at the right edge of their row, so this rarely bites; it is
    // here so every non-portal menu in the app carries the same guarantee.
    if (this._getClipContainer) {
      // Fixed positioning escapes the clip container's `overflow: auto`
      // clipping entirely — keepMenuInViewport assumes position:absolute +
      // a transform and does not apply to a fixed-mode menu.
      positionMenuFixedBelow(menu, button);
      const clipEl = this._getClipContainer();
      // A fixed menu must not float over a row that scrolled away underneath
      // it — close on scroll of either the list's own scrollport or the page.
      clipEl?.addEventListener('scroll', this._boundClose, { passive: true });
      window.addEventListener('scroll', this._boundClose, { passive: true });
      window.addEventListener('resize', this._boundClose);
    } else {
      keepMenuInViewport(menu);
    }

    document.addEventListener('click', this._boundClose);
    // The menu is a DOM child of the row, so this fires only once the cursor
    // has left BOTH the row and the open menu — hover-out closes everything.
    if (this.hoverClose) row.addEventListener('mouseleave', this._boundClose);
  }

  /** Close the open row menu (if any) and release listeners. Safe to call unconditionally. */
  close() {
    document.removeEventListener('click', this._boundClose);
    if (this._rowEl) {
      this._rowEl.removeEventListener('mouseleave', this._boundClose);
      this._rowEl = null;
    }
    if (this._getClipContainer) {
      this._getClipContainer()?.removeEventListener('scroll', this._boundClose);
      window.removeEventListener('scroll', this._boundClose);
      window.removeEventListener('resize', this._boundClose);
    }
    if (this._openKey === null) return;
    this._openKey = null;

    const container = this._getContainer();
    const row = container?.querySelector(`${this.rowSelector}.${this.openClass}`);
    if (!row) return;
    row.classList.remove(this.openClass);
    row.querySelector('.note-row__options')?.setAttribute('aria-expanded', 'false');
    const menu = row.querySelector('.options-dropdown__menu');
    menu?.classList.add('options-dropdown__menu--hidden');
    if (this._getClipContainer && menu) {
      // Reverts to the stylesheet's default (position: absolute; right: 0;
      // top: calc(100% + 4px)) so the next open recomputes from scratch —
      // matches positionMenuFixedBelow's own re-measure-from-clean contract.
      menu.style.position = '';
      menu.style.top = '';
      menu.style.left = '';
      menu.style.right = '';
    }
  }

  /**
   * Right-click a row to open its options menu in place of the browser's
   * native context menu — same menu the hover-revealed kebab button opens,
   * just reached a second way (mirrors the sidebar recents' row context menu).
   * @param {MouseEvent} event
   */
  handleContextMenu(event) {
    const row = event.target.closest(this.rowSelector);
    const container = this._getContainer();
    if (!row || !container?.contains(row)) return;
    const button = row.querySelector('[data-action="row-options"]');
    if (!button) return;
    event.preventDefault();
    this.toggle(button);
  }
}
